import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { showError, showSuccess } from '@/lib/swal'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { loanRequestService } from '@/services/loanRequestService'
import { userService } from '@/services/userService'
import { employeesService } from '@/services/employeesService'
import type { LoanAccountItem, LoanLookupItem, LoanRequestVm } from '@/api/types'

function todayIso() {
  const d = new Date()
  return d.toISOString().slice(0, 10)
}

export const LoanRequestDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0
  const userId = user?.userId ?? 0

  const isNew = params.id === 'new' || !params.id
  const editId = !isNew ? Number(params.id) : 0

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [loanTypes, setLoanTypes] = useState<LoanLookupItem[]>([])
  const [accounts, setAccounts] = useState<LoanAccountItem[]>([])
  const [statusLookups, setStatusLookups] = useState<LoanLookupItem[]>([])
  const [statusId, setStatusId] = useState(0)
  const [statusName, setStatusName] = useState('')

  const [loanTypeId, setLoanTypeId] = useState<number>(0)
  const [startDate, setStartDate] = useState<string>(todayIso())
  const [loanAmount, setLoanAmount] = useState<string>('0')
  const [noOfInstallment, setNoOfInstallment] = useState<string>('0')
  const [deductAmount, setDeductAmount] = useState<string>('0')
  const [creditAccount, setCreditAccount] = useState<number>(0)
  const [details, setDetails] = useState('')
  const [attachmentUrl, setAttachmentUrl] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)

  const req = t('loanRequestPage.requiredMark')

  useEffect(() => {
    if (!branchId) return
    const loadLookups = async () => {
      try {
        const [typeList, accList, statusList] = await Promise.all([
          loanRequestService.getLoanTypes(),
          loanRequestService.getAccounts(branchId),
          loanRequestService.getStatusLookups(),
        ])
        setLoanTypes(typeList || [])
        setAccounts(accList || [])
        setStatusLookups(statusList || [])
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? t('loanRequestPage.loadLookupsFailed'))
      }
    }
    loadLookups()
  }, [branchId])

  useEffect(() => {
    if (!branchId || isNew || !editId) return
    const load = async () => {
      try {
        setLoading(true)
        const vm = await loanRequestService.getById(editId, branchId)
        if (!vm) return
        setLoanTypeId(vm.loanTypeId ?? 0)
        setStartDate(vm.startDate ? String(vm.startDate).slice(0, 10) : todayIso())
        setLoanAmount(String(vm.loanAmount ?? 0))
        setNoOfInstallment(String(vm.noOfInstallment ?? 0))
        setDeductAmount(String(vm.deductAmount ?? 0))
        setCreditAccount(vm.creditAccount ?? 0)
        setDetails(vm.details ?? '')
        setAttachmentUrl(vm.attachmentUrl ?? '')
        setStatusId(Number(vm.status ?? 0))
        setStatusName(String(vm.statusName ?? ''))
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? t('loanRequestPage.loadFailed'))
        navigate('/self-services/loan-requests')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, isNew, editId, navigate])

  // Whole-number installment = floor(loan / installments). Last payroll installment takes the remainder.
  useEffect(() => {
    const loan = Math.floor(Number(loanAmount) || 0)
    const installments = Math.floor(Number(noOfInstallment) || 0)
    if (installments > 0 && loan >= 0) {
      setDeductAmount(String(Math.floor(loan / installments)))
    }
  }, [loanAmount, noOfInstallment])

  const lookupLabel = (x: LoanLookupItem) =>
    x.lookupNameEn || x.lookupNameAr || x.nameEn || x.nameAr || x.name || String(x.codeType ?? x.id)

  const resolvedStatusName = (
    statusLookups.find((s) => s.id === statusId)?.lookupNameEn ||
    statusName ||
    ''
  ).toLowerCase()
  const isApproved = resolvedStatusName === 'approved' || resolvedStatusName.includes('approved') || statusId === 435
  const isRejected = resolvedStatusName === 'rejected' || resolvedStatusName.includes('rejected') || statusId === 436
  const isLocked = !isNew && (isApproved || isRejected)

  const accountLabel = (a: LoanAccountItem) => {
    const name = a.accountName || a.nameEn || a.nameAr || ''
    const code = a.accountCode ? ` (${a.accountCode})` : ''
    const base = `${name}${code}`.trim()
    return base || t('loanRequestPage.accountFallback', { id: a.id })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLocked) {
      await showError(
        t('loanRequestPage.updateFailedTitle'),
        isApproved ? t('loanRequestPage.viewOnlyApproved') : t('loanRequestPage.viewOnlyRejected')
      )
      return
    }
    if (!branchId) return
    if (!loanTypeId) {
      await showError(t('loanRequestPage.saveFailedTitle'), t('loanRequestPage.toastLoanType'))
      return
    }
    if (!creditAccount) {
      await showError(t('loanRequestPage.saveFailedTitle'), t('loanRequestPage.toastCreditAccount'))
      return
    }
    if (!startDate) {
      await showError(t('loanRequestPage.saveFailedTitle'), t('loanRequestPage.toastLoanRequestDate'))
      return
    }
    if (!details.trim()) {
      await showError(t('loanRequestPage.saveFailedTitle'), t('loanRequestPage.toastDetails'))
      return
    }

    const vm: LoanRequestVm = {
      id: isNew ? 0 : editId,
      startDate,
      loanAmount: Math.floor(Number(loanAmount || 0)),
      noOfInstallment: Math.floor(Number(noOfInstallment || 0)),
      deductAmount: Math.floor(Number(deductAmount || 0)),
      loanTypeId,
      details,
      status: 0,
      branchId,
      creditAccount,
      attachmentUrl: attachmentUrl || undefined,
      empId: 0,
      departmentId: 0,
    }

    try {
      setSaving(true)

      // Build departmentId & empId from logged-in user (self-service flow)
      const u = userId ? await userService.getUserById(branchId, userId) : null
      const computedEmpId = u?.empId ?? u?.employeeId ?? 0
      if (!computedEmpId) {
        await showError(t('loanRequestPage.saveFailedTitle'), t('loanRequestPage.toastEmployeeLink'))
        return
      }

      const emp = await employeesService.getEmployeeById(branchId, computedEmpId)
      const computedDepartmentId = (emp as any)?.empDepartmetId ?? (emp as any)?.departmentId ?? 0
      if (!computedDepartmentId) {
        await showError(t('loanRequestPage.saveFailedTitle'), t('loanRequestPage.toastDepartmentLink'))
        return
      }

      vm.empId = computedEmpId
      vm.departmentId = computedDepartmentId

      if (isNew) {
        await loanRequestService.create(vm, file)
        await showSuccess(t('loanRequestPage.savedTitle'), t('loanRequestPage.savedMessage'), 3000)
      } else {
        await loanRequestService.update(vm, file)
        await showSuccess(t('loanRequestPage.updatedTitle'), t('loanRequestPage.updatedMessage'), 3000)
      }
      navigate('/self-services/loan-requests')
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? t('loanRequestPage.saveFailed')
      await showError(isNew ? t('loanRequestPage.saveFailedTitle') : t('loanRequestPage.updateFailedTitle'), msg, 5000)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('loanRequestPage.pageTitle')}
        description={t('loanRequestPage.pageDescription')}
      />

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="p-6 space-y-6 overflow-visible">
            {isLocked ? (
              <p className="text-sm text-muted-foreground">
                {isApproved ? t('loanRequestPage.viewOnlyApproved') : t('loanRequestPage.viewOnlyRejected')}
              </p>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <SearchableSelect
                  disabled={isLocked}
                  label={`${req} ${t('loanRequestPage.labelLoanType')}`}
                  options={[
                    { value: '0', label: t('loanRequestPage.placeholderSelect') },
                    ...loanTypes.filter((x) => x.id).map((x) => ({
                      value: String(x.id),
                      label: lookupLabel(x),
                    })) as SearchableSelectOption[],
                  ]}
                  value={loanTypeId ? String(loanTypeId) : '0'}
                  onValueChange={(v) => setLoanTypeId(Number(v) || 0)}
                  placeholder={t('loanRequestPage.placeholderSelect')}
                  searchPlaceholder={t('loanRequestPage.searchLoanType')}
                  emptyMessage={t('loanRequestPage.emptyLoanType')}
                />
              </div>

              <div className="space-y-2">
                <Label>{req} {t('loanRequestPage.labelLoanRequestDate')}</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={isLocked} readOnly={isLocked} className={isLocked ? 'bg-muted' : undefined} />
              </div>

              <div className="space-y-2">
                <Label>{req} {t('loanRequestPage.labelLoanAmount')}</Label>
                <Input inputMode="decimal" value={loanAmount} onChange={(e) => setLoanAmount(e.target.value)} disabled={isLocked} readOnly={isLocked} className={isLocked ? 'bg-muted' : undefined} />
              </div>

              <div className="space-y-2">
                <Label>{req} {t('loanRequestPage.labelNoOfInstallment')}</Label>
                <Input inputMode="numeric" value={noOfInstallment} onChange={(e) => setNoOfInstallment(e.target.value)} disabled={isLocked} readOnly={isLocked} className={isLocked ? 'bg-muted' : undefined} />
              </div>

              <div className="space-y-2">
                <Label>{req} {t('loanRequestPage.labelDeductionPerMonth')}</Label>
                <Input
                  inputMode="numeric"
                  value={deductAmount}
                  readOnly
                  className="bg-muted"
                  title={t('loanRequestPage.deductTooltip')}
                />
              </div>

              <div className="space-y-2">
                <SearchableSelect
                  disabled={isLocked}
                  label={`${req} ${t('loanRequestPage.labelCreditAccount')}`}
                  options={[
                    { value: '0', label: t('loanRequestPage.placeholderSelect') },
                    ...accounts.filter((a) => a.id).map((a) => ({
                      value: String(a.id),
                      label: accountLabel(a),
                    })) as SearchableSelectOption[],
                  ]}
                  value={creditAccount ? String(creditAccount) : '0'}
                  onValueChange={(v) => setCreditAccount(Number(v) || 0)}
                  placeholder={t('loanRequestPage.placeholderSelect')}
                  searchPlaceholder={t('loanRequestPage.searchAccount')}
                  emptyMessage={t('loanRequestPage.emptyAccount')}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('loanRequestPage.labelAttachment')}</Label>
                <Input
                  type="file"
                  disabled={isLocked}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                {attachmentUrl ? (
                  <p className="text-xs text-muted-foreground">{t('loanRequestPage.attachmentExisting')} {attachmentUrl}</p>
                ) : null}
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>{req} {t('loanRequestPage.labelDetails')}</Label>
                <Textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder={t('loanRequestPage.placeholderDetails')} disabled={isLocked} readOnly={isLocked} className={isLocked ? 'bg-muted' : undefined} />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" disabled={saving || loading} onClick={() => navigate('/self-services/loan-requests')}>
                {isLocked ? t('loanRequestPage.backToList') : t('loanRequestPage.cancel')}
              </Button>
              {!isLocked ? (
                <Button type="submit" disabled={saving || loading}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? t('loanRequestPage.saving') : isNew ? t('loanRequestPage.save') : t('loanRequestPage.update')}
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
